import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Calendar,
  ChevronRight,
  Crown,
  Filter,
  Heart,
  Loader2,
  MapPin,
  PenTool,
  RefreshCw,
  Search,
  Sparkles,
  Star,
  Trash2,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { useInvitation, PLANS } from '../contexts/InvitationContext';
import { useAuth } from '../contexts/AuthContext';
import { InvitationPreview, formatDisplayDate, formatDisplayTime } from '../components/invitation/InvitationPreview';
import { Footer } from '../components/layout/Footer';
import { MigrationBanner } from '../components/library/MigrationBanner';

const styleFilters = ['all', 'Romantic Classic', 'Soft Modern', 'Floral Boho', 'Minimal Luxe'];

const statCards = (savedCount, favoriteCount, userPlan, limit) => [
  {
    label: 'Saved invitations',
    value: savedCount,
    note: limit.max === Infinity ? 'Unlimited archive enabled' : `${Math.max(limit.max - limit.current, 0)} slots left`,
    icon: Sparkles
  },
  {
    label: 'Favorites',
    value: favoriteCount,
    note: favoriteCount > 0 ? 'Pinned for quick access' : 'Mark your strongest concepts',
    icon: Heart
  },
  {
    label: 'Plan',
    value: PLANS[userPlan]?.name || userPlan,
    note: userPlan === 'HEIRLOOM' ? 'Priority exports and unlimited storage' : 'Upgrade for premium capacity',
    icon: Crown
  }
];

const sortOptions = [
  { value: 'recent', label: 'Recently updated' },
  { value: 'favorites', label: 'Favorites first' },
  { value: 'style', label: 'Style name' },
  { value: 'date', label: 'Wedding date' }
];

function EmptyLibraryState() {
  return (
    <section className="rounded-[2rem] border border-[#E6DCCB] bg-white px-8 py-14 text-center shadow-[0_20px_80px_rgba(79,55,32,0.06)]">
      <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-[#FBF3E7] text-[#BA8A2A]">
        <Sparkles size={28} />
      </div>
      <h2 className="mb-3 font-serif text-3xl text-[#1F1A17]">Your invitation library starts here</h2>
      <p className="mx-auto mb-8 max-w-xl text-base leading-7 text-[#7B6F63]">
        Every concept you craft, save, and favorite will live here in a calmer dashboard designed for easy comparison and quick edits.
      </p>
      <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
        <Link
          to="/create"
          className="inline-flex items-center justify-center rounded-full bg-[#1E1A17] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#312922]"
        >
          <PenTool size={16} className="mr-2" />
          Create your first invitation
        </Link>
        <Link
          to="/pricing"
          className="inline-flex items-center justify-center rounded-full border border-[#DCC7A5] px-6 py-3 text-sm font-semibold text-[#7F5A1C] transition hover:bg-[#FCF6EC]"
        >
          Explore premium plans
        </Link>
      </div>
    </section>
  );
}

export default function LibraryPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const {
    savedInvitations,
    deleteInvitation,
    toggleFavorite,
    loadInvitation,
    userPlan,
    getSaveLimit,
    isLoading,
    refreshInvitations
  } = useInvitation();

  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [filterStyle, setFilterStyle] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const limit = getSaveLimit();

  useEffect(() => {
    if (isAuthenticated) {
      refreshInvitations();
    }
  }, [isAuthenticated, refreshInvitations]);

  const filteredInvitations = useMemo(() => {
    let result = [...savedInvitations];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((inv) => {
        const name1 = inv.couple_names?.name1 || '';
        const name2 = inv.couple_names?.name2 || '';
        return (
          name1.toLowerCase().includes(query) ||
          name2.toLowerCase().includes(query) ||
          (inv.location || '').toLowerCase().includes(query) ||
          (inv.style || '').toLowerCase().includes(query)
        );
      });
    }

    if (filterStyle !== 'all') {
      result = result.filter((inv) => inv.style === filterStyle);
    }

    switch (sortBy) {
      case 'recent':
        result.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        break;
      case 'favorites':
        result.sort((a, b) => (b.favorite ? 1 : 0) - (a.favorite ? 1 : 0));
        break;
      case 'style':
        result.sort((a, b) => (a.style || '').localeCompare(b.style || ''));
        break;
      case 'date':
        result.sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));
        break;
      default:
        break;
    }

    return result;
  }, [savedInvitations, searchQuery, sortBy, filterStyle]);

  const stats = statCards(
    savedInvitations.length,
    savedInvitations.filter((inv) => inv.favorite).length,
    userPlan,
    limit
  );

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this invitation?')) {
      return;
    }

    const success = await deleteInvitation(id);
    if (success) {
      toast.success('Invitation deleted');
      setSelectedInvitation(null);
      return;
    }

    toast.error('Failed to delete invitation');
  };

  const handleEdit = (invitation) => {
    const loadedInvitation = loadInvitation(invitation);
    if (!loadedInvitation) {
      toast.error('Unable to open this invitation for editing.');
      return;
    }

    setSelectedInvitation(null);
    navigate('/create', {
      state: {
        mode: 'edit',
        invitationId: loadedInvitation.id,
      },
    });
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refreshInvitations();
    setIsRefreshing(false);
    toast.success('Library refreshed');
  };

  return (
    <div className="min-h-screen bg-[#F8F3EC] pb-28 md:pb-0">
      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
        <MigrationBanner />

        <section className="overflow-hidden rounded-[2rem] border border-[#E5D5BF] bg-[radial-gradient(circle_at_top_left,_rgba(255,255,255,0.96),_rgba(247,239,229,0.82)_46%,_rgba(238,225,214,0.72)_100%)] shadow-[0_24px_90px_rgba(79,55,32,0.08)]">
          <div className="grid gap-10 border-b border-[#E6DCCB] px-6 py-8 md:grid-cols-[1.35fr_0.65fr] md:px-10 md:py-10">
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#E6D6BC] bg-white/80 px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-[#8C785E]">
                <Sparkles size={14} className="text-[#B8872B]" />
                Invitation Studio
              </div>
              <h1 className="max-w-3xl font-serif text-4xl leading-[1.05] text-[#1F1A17] md:text-6xl">
                Your invitation dashboard, refined for review, editing, and fast delivery.
              </h1>
              <p className="mt-5 max-w-2xl text-base leading-8 text-[#7B6F63] md:text-lg">
                Organize every draft like a curated collection. Compare styles, favorite your strongest concepts, and move from saved design to final export without friction.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/create"
                  className="inline-flex items-center justify-center rounded-full bg-[#1E1A17] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#312922]"
                >
                  <PenTool size={16} className="mr-2" />
                  Create a new invitation
                </Link>
                <button
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="inline-flex items-center justify-center rounded-full border border-[#DDC9AA] bg-white/70 px-6 py-3 text-sm font-semibold text-[#7C5A20] transition hover:bg-[#FEFBF6] disabled:opacity-60"
                >
                  <RefreshCw size={16} className={`mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                  Refresh collection
                </button>
              </div>
            </div>

            <div className="rounded-[1.75rem] border border-white/80 bg-white/85 p-5 shadow-[0_20px_50px_rgba(84,62,41,0.08)]">
              <div className="mb-4 flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8D7E70]">Archive status</p>
                <span className="rounded-full bg-[#FCF4E7] px-3 py-1 text-[11px] font-semibold text-[#A97821]">
                  {limit.max === Infinity ? 'Unlimited' : `${limit.current}/${limit.max}`}
                </span>
              </div>
              <div className="space-y-4">
                {stats.map((stat) => {
                  const Icon = stat.icon;
                  return (
                    <div key={stat.label} className="rounded-[1.35rem] border border-[#EEE2D2] bg-[#FFFDFA] p-4">
                      <div className="mb-3 flex items-center justify-between">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FBF3E6] text-[#B8872B]">
                          <Icon size={18} />
                        </div>
                        <span className="text-2xl font-semibold text-[#251E1A]">{stat.value}</span>
                      </div>
                      <p className="text-sm font-semibold text-[#43362C]">{stat.label}</p>
                      <p className="mt-1 text-sm leading-6 text-[#8B7E72]">{stat.note}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="px-6 py-6 md:px-10">
            <div className="flex flex-col gap-4 rounded-[1.75rem] border border-[#E8D9C5] bg-white/78 p-4 md:p-5">
              <div className="flex flex-col gap-3 md:flex-row md:items-center">
                <div className="relative flex-1">
                  <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9B8F83]" />
                  <input
                    type="text"
                    placeholder="Search by couple name, style, or venue"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    className="w-full rounded-full border border-[#E8D9C5] bg-[#FFFDF8] py-3 pl-11 pr-4 text-sm text-[#2A211C] outline-none transition focus:border-[#CDA865] focus:ring-2 focus:ring-[#E7CC95]/40"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <select
                    value={sortBy}
                    onChange={(event) => setSortBy(event.target.value)}
                    className="rounded-full border border-[#E8D9C5] bg-[#FFFDF8] px-4 py-3 text-sm text-[#5E5247] outline-none transition focus:border-[#CDA865]"
                  >
                    {sortOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => setShowFilters((current) => !current)}
                    className={`inline-flex items-center justify-center rounded-full border px-4 py-3 text-sm font-medium transition ${
                      showFilters
                        ? 'border-[#CDA865] bg-[#FDF3DF] text-[#8A611A]'
                        : 'border-[#E8D9C5] bg-[#FFFDF8] text-[#6B5D52]'
                    }`}
                  >
                    <Filter size={16} className="mr-2" />
                    Filters
                  </button>
                </div>
              </div>

              {showFilters && (
                <div className="border-t border-[#EEE2D2] pt-4">
                  <div className="flex flex-wrap gap-2">
                    {styleFilters.map((style) => (
                      <button
                        key={style}
                        onClick={() => setFilterStyle(style)}
                        className={`rounded-full px-4 py-2 text-sm transition ${
                          filterStyle === style
                            ? 'bg-[#1E1A17] text-white'
                            : 'bg-[#FBF7F0] text-[#74675C] hover:bg-[#F2E9DB]'
                        }`}
                      >
                        {style === 'all' ? 'All styles' : style}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        <div className="mt-8">
          {isLoading && savedInvitations.length === 0 ? (
            <div className="rounded-[2rem] border border-[#E6DCCB] bg-white px-8 py-16 text-center shadow-[0_20px_80px_rgba(79,55,32,0.06)]">
              <Loader2 className="mx-auto mb-4 animate-spin text-[#B8872B]" size={30} />
              <p className="text-[#7B6F63]">Loading your invitation collection...</p>
            </div>
          ) : savedInvitations.length === 0 ? (
            <EmptyLibraryState />
          ) : filteredInvitations.length === 0 ? (
            <section className="rounded-[2rem] border border-[#E6DCCB] bg-white px-8 py-16 text-center shadow-[0_20px_80px_rgba(79,55,32,0.06)]">
              <p className="text-lg font-medium text-[#2B241F]">No invitations match this search.</p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setFilterStyle('all');
                }}
                className="mt-3 text-sm font-semibold text-[#A97821] hover:underline"
              >
                Clear filters
              </button>
            </section>
          ) : (
            <section className="space-y-5">
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#9D8C7B]">Collection</p>
                  <h2 className="mt-2 font-serif text-3xl text-[#211A17]">Saved invitation concepts</h2>
                </div>
                <p className="hidden text-sm text-[#8D7F71] md:block">
                  {filteredInvitations.length} result{filteredInvitations.length === 1 ? '' : 's'}
                </p>
              </div>

              <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                {filteredInvitations.map((invitation) => (
                  <article
                    key={invitation.id}
                    className="group overflow-hidden rounded-[1.75rem] border border-[#E7DAC9] bg-white shadow-[0_18px_60px_rgba(79,55,32,0.07)] transition hover:-translate-y-1 hover:shadow-[0_24px_80px_rgba(79,55,32,0.1)]"
                  >
                    <div
                      className="relative cursor-pointer overflow-hidden bg-[linear-gradient(180deg,#FDF9F2_0%,#F8F0E6_100%)] px-6 py-7"
                      onClick={() => setSelectedInvitation(invitation)}
                    >
                      <div className="absolute inset-x-5 top-5 flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.16em] text-[#978673]">
                        <span>{invitation.style || 'Classic'}</span>
                        {invitation.favorite && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-white/90 px-2 py-1 text-[#A97821]">
                            <Star size={11} fill="#A97821" />
                            Favorite
                          </span>
                        )}
                      </div>

                      <div className="flex justify-center pt-7">
                        <InvitationPreview
                          invitation={invitation}
                          size="medium"
                          className="origin-center scale-[0.95] transition duration-500 group-hover:scale-100"
                        />
                      </div>
                    </div>

                    <div className="space-y-4 px-6 py-5">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="font-serif text-2xl leading-tight text-[#211A17]">
                            {invitation.couple_names?.name1 || 'Partner'} & {invitation.couple_names?.name2 || 'Partner'}
                          </h3>
                          <p className="mt-1 text-sm text-[#8A7B6F]">
                            Saved {new Date(invitation.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <button
                          onClick={() => toggleFavorite(invitation.id)}
                          className={`rounded-full border p-2 transition ${
                            invitation.favorite
                              ? 'border-[#E7CF9B] bg-[#FCF3E0] text-[#B8872B]'
                              : 'border-[#E8DCCD] bg-[#FFFDFA] text-[#B2A69B] hover:border-[#DAC4A6]'
                          }`}
                          aria-label="Toggle favorite"
                        >
                          <Heart size={16} fill={invitation.favorite ? 'currentColor' : 'none'} />
                        </button>
                      </div>

                      <div className="grid gap-3 text-sm text-[#4E433A] md:grid-cols-2">
                        <div className="rounded-2xl bg-[#FBF7F0] px-4 py-3">
                          <div className="mb-1 flex items-center gap-2 text-[#9C8E81]">
                            <Calendar size={14} />
                            <span className="text-xs font-semibold uppercase tracking-[0.15em]">Date</span>
                          </div>
                          <p className="leading-6">{formatDisplayDate(invitation.date)}</p>
                        </div>
                        <div className="rounded-2xl bg-[#FBF7F0] px-4 py-3">
                          <div className="mb-1 flex items-center gap-2 text-[#9C8E81]">
                            <MapPin size={14} />
                            <span className="text-xs font-semibold uppercase tracking-[0.15em]">Venue</span>
                          </div>
                          <p className="line-clamp-2 leading-6">{invitation.location || 'Venue to be confirmed'}</p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-3">
                        <button
                          onClick={() => handleEdit(invitation)}
                          className="inline-flex items-center justify-center rounded-full bg-[#1E1A17] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#312922]"
                        >
                          Edit invitation
                        </button>
                        <button
                          onClick={() => setSelectedInvitation(invitation)}
                          className="inline-flex items-center gap-2 text-sm font-semibold text-[#836433] transition hover:text-[#A97821]"
                        >
                          View details
                          <ChevronRight size={16} />
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}
        </div>

        {savedInvitations.length > 0 && userPlan !== 'HEIRLOOM' && (
          <section className="mt-8 overflow-hidden rounded-[2rem] border border-[#E9D9BE] bg-[linear-gradient(135deg,rgba(255,249,238,0.98),rgba(250,239,221,0.95))] px-6 py-7 shadow-[0_18px_60px_rgba(79,55,32,0.06)] md:px-8">
            <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
              <div className="max-w-2xl">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#A07B39]">Heirloom upgrade</p>
                <h3 className="mt-2 font-serif text-3xl text-[#241D18]">Unlock a deeper archive and premium exports.</h3>
                <p className="mt-3 text-base leading-7 text-[#7A6B5F]">
                  Save unlimited invitations, keep your strongest concepts pinned, and move into higher-touch delivery flows as your studio grows.
                </p>
              </div>
              <Link
                to="/#pricing"
                className="inline-flex items-center justify-center rounded-full bg-[#1E1A17] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#312922]"
              >
                View plans
              </Link>
            </div>
          </section>
        )}
      </div>

      {selectedInvitation && (
        <div className="fixed inset-0 z-50 bg-[#1A1511]/55 p-4 backdrop-blur-sm" onClick={() => setSelectedInvitation(null)}>
          <div
            className="mx-auto max-h-[92vh] max-w-5xl overflow-auto rounded-[2rem] border border-white/60 bg-[#FFFCF7] shadow-[0_30px_120px_rgba(0,0,0,0.22)]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[#ECE1D2] bg-[#FFFCF7]/95 px-6 py-5 backdrop-blur md:px-8">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9E8E7F]">Invitation details</p>
                <h2 className="mt-2 font-serif text-3xl text-[#211A17]">
                  {selectedInvitation.couple_names?.name1 || 'Partner'} & {selectedInvitation.couple_names?.name2 || 'Partner'}
                </h2>
              </div>
              <button
                onClick={() => setSelectedInvitation(null)}
                className="rounded-full border border-[#E8DCCD] bg-white p-2 text-[#8E8175] transition hover:text-[#251D18]"
              >
                <X size={18} />
              </button>
            </div>

            <div className="grid gap-8 px-6 py-6 md:grid-cols-[0.88fr_1.12fr] md:px-8 md:py-8">
              <div className="rounded-[1.8rem] border border-[#E8DCCD] bg-[linear-gradient(180deg,#FDF9F2_0%,#F8F0E6_100%)] p-6">
                <div className="flex justify-center">
                  <InvitationPreview invitation={selectedInvitation} size="large" />
                </div>
              </div>

              <div className="space-y-6">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-[1.5rem] border border-[#E8DCCD] bg-white p-5">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#A08F7E]">Date</p>
                    <p className="mt-2 font-serif text-2xl text-[#241C18]">{formatDisplayDate(selectedInvitation.date)}</p>
                    <p className="mt-2 text-sm text-[#7D7064]">{formatDisplayTime(selectedInvitation.time) || 'Time not set yet'}</p>
                  </div>
                  <div className="rounded-[1.5rem] border border-[#E8DCCD] bg-white p-5">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#A08F7E]">Style</p>
                    <p className="mt-2 font-serif text-2xl text-[#241C18]">{selectedInvitation.style || 'Classic'}</p>
                    <p className="mt-2 text-sm text-[#7D7064]">{selectedInvitation.dress_code || 'Dress code not set'}</p>
                  </div>
                </div>

                <div className="rounded-[1.5rem] border border-[#E8DCCD] bg-white p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#A08F7E]">Venue</p>
                  <p className="mt-2 text-lg leading-8 text-[#342A24]">
                    {selectedInvitation.location || 'Venue details are still being refined.'}
                  </p>
                </div>

                <div className="rounded-[1.5rem] border border-[#E8DCCD] bg-white p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#A08F7E]">Archive notes</p>
                  <div className="mt-4 grid gap-3 text-sm text-[#64574C] sm:grid-cols-2">
                    <div className="rounded-2xl bg-[#FBF7F0] px-4 py-3">Created {new Date(selectedInvitation.created_at).toLocaleDateString()}</div>
                    <div className="rounded-2xl bg-[#FBF7F0] px-4 py-3">
                      {selectedInvitation.favorite ? 'Marked as a favorite concept' : 'Not favorited yet'}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row">
                  <button
                    onClick={() => handleEdit(selectedInvitation)}
                    className="inline-flex flex-1 items-center justify-center rounded-full bg-[#1E1A17] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#312922]"
                  >
                    Edit invitation
                  </button>
                  <button
                    onClick={() => toggleFavorite(selectedInvitation.id)}
                    className="inline-flex items-center justify-center rounded-full border border-[#E7CF9B] bg-[#FCF3E0] px-6 py-3 text-sm font-semibold text-[#A97821] transition hover:bg-[#F8ECCF]"
                  >
                    <Heart size={16} className="mr-2" fill={selectedInvitation.favorite ? 'currentColor' : 'none'} />
                    {selectedInvitation.favorite ? 'Favorited' : 'Add to favorites'}
                  </button>
                  <button
                    onClick={() => handleDelete(selectedInvitation.id)}
                    className="inline-flex items-center justify-center rounded-full border border-[#F2C8C2] bg-white px-5 py-3 text-sm font-semibold text-[#C0554D] transition hover:bg-[#FFF4F2]"
                  >
                    <Trash2 size={16} className="mr-2" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
